import { useState } from "react";
import { Overlay } from "../../../../styles/popup";
import {
  MissionBingoColumnType,
  MissionBingoRowRewardDetailType,
} from "../../../../api/pub-mission-bingo";
import ProgressResetConfirmPopup from "../../../event/shared/progress-reset-confirm-popup";
import {
  getCellDisplayNumber,
  getMaxLineCount,
  useBingoForm,
} from "../../hooks/use-bingo-form";
import * as S from "./styles";

interface BingoFormPopupProps {
  pubId: number;
  bingoId?: number;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const BINGO_INFO = `FREE 칸 없음. 가로/세로/대각 인정.
3x3 최대 8줄 / 4x4 최대 10줄.
마지막 칸으로 동시에 여러 줄 완성 시 모두 인정.
진행 기간: 시작일 09:00 ~ 종료일 다음날 09:00.
유저당 1회 참여, 완료 후 재참여 불가.
기간 종료 후에도 달성한 보상은 수령 가능.`;

const BINGO_RESET_PHRASE = "빙고 진행 상황을 초기화합니다";

const getCellTypeLabel = (cell: {
  type: MissionBingoColumnType;
  count: string;
}) => {
  if (cell.type === MissionBingoColumnType.CUMULATIVE) {
    return `누적형 / ${cell.count || 0}`;
  }
  return "완료형";
};

const BingoFormPopup = ({
  pubId,
  bingoId,
  onClose,
  onSuccess,
}: BingoFormPopupProps) => {
  const {
    form,
    isEdit,
    canEdit,
    loading,
    submitting,
    resetting,
    selectedCell,
    selectedCellKey,
    setSelectedCellKey,
    updateField,
    updateCell,
    addStage,
    removeStage,
    updateStage,
    addStageReward,
    removeStageReward,
    updateStageReward,
    submit,
    resetProgress,
  } = useBingoForm({ pubId, bingoId, onClose, onSuccess });

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

  const disabled = !canEdit || submitting;
  const maxLineCount = getMaxLineCount(form.column);

  const handleResetConfirm = async () => {
    try {
      await resetProgress();
      setIsResetConfirmOpen(false);
    } catch {
      // error already alerted in hook
    }
  };

  return (
    <Overlay $zIndex={1000}>
      <S.Popup>
        <S.Header>
          <div className="title">{isEdit ? "빙고 수정" : "빙고 등록"}</div>
          <img
            src="/image-web/tournament/close.svg"
            alt="close"
            onClick={onClose}
          />
        </S.Header>

        {loading ? (
          <S.LoadingMessage>빙고 정보를 불러오는 중입니다.</S.LoadingMessage>
        ) : (
          <>
            <S.Body>
              {!canEdit && (
                <S.DisabledNotice>
                  <div className="message">
                    진행 중인 유저가 있어 빙고를 수정할 수 없습니다.
                  </div>
                  <S.ResetProgressButton
                    type="button"
                    disabled={resetting}
                    onClick={() => setIsResetConfirmOpen(true)}
                  >
                    진행 초기화
                  </S.ResetProgressButton>
                </S.DisabledNotice>
              )}

              <S.Section>
                <S.SectionTitle>기본 정보</S.SectionTitle>

                <S.Field>
                  <S.Label>
                    빙고명 <span className="required">*</span>
                  </S.Label>
                  <S.TextInput
                    placeholder="빙고명을 입력하세요"
                    value={form.name}
                    disabled={disabled}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </S.Field>

                <S.DateRow>
                  <S.Field>
                    <S.Label>
                      시작일 <span className="required">*</span>
                    </S.Label>
                    <S.TextInput
                      type="date"
                      value={form.startDate}
                      disabled={disabled}
                      onChange={(e) => updateField("startDate", e.target.value)}
                    />
                    <S.SubText>시작일 09:00부터 진행됩니다</S.SubText>
                  </S.Field>
                  <S.Field>
                    <S.Label>
                      종료일 <span className="required">*</span>
                    </S.Label>
                    <S.TextInput
                      type="date"
                      value={form.endDate}
                      disabled={disabled}
                      onChange={(e) => updateField("endDate", e.target.value)}
                    />
                    <S.SubText>종료일 다음날 09:00까지 진행됩니다</S.SubText>
                  </S.Field>
                </S.DateRow>

                <S.Field>
                  <S.Label>
                    빙고 규격 <span className="required">*</span>
                  </S.Label>
                  <S.RadioGroup>
                    <S.RadioOption>
                      <input
                        type="radio"
                        checked={form.column === 3}
                        disabled={disabled}
                        onChange={() => updateField("column", 3)}
                      />
                      3×3
                    </S.RadioOption>
                    <S.RadioOption>
                      <input
                        type="radio"
                        checked={form.column === 4}
                        disabled={disabled}
                        onChange={() => updateField("column", 4)}
                      />
                      4×4
                    </S.RadioOption>
                  </S.RadioGroup>
                </S.Field>

                <S.InfoBox>{BINGO_INFO}</S.InfoBox>
              </S.Section>

              <S.Section>
                <S.SectionTitle>빙고판 설정</S.SectionTitle>

                <S.BoardLayout>
                  <S.BoardGridWrap>
                    <S.BoardGrid $size={form.column}>
                      {form.cells.map((cell) => {
                        const configured = Boolean(cell.name.trim());
                        const displayNumber = getCellDisplayNumber(
                          cell.row,
                          cell.column,
                          form.column,
                        );

                        return (
                          <S.BoardCell
                            key={cell.key}
                            type="button"
                            $selected={selectedCellKey === cell.key}
                            $configured={configured}
                            disabled={disabled}
                            onClick={() => setSelectedCellKey(cell.key)}
                          >
                            <div className="number">칸 {displayNumber}</div>
                            <div className="name">
                              {configured ? cell.name : "미설정"}
                            </div>
                            <div className="type">
                              {configured
                                ? getCellTypeLabel(cell)
                                : "완료형"}
                            </div>
                          </S.BoardCell>
                        );
                      })}
                    </S.BoardGrid>
                    <S.SubText style={{ marginTop: 10 }}>
                      칸을 클릭하면 우측에서 설정할 수 있습니다.
                    </S.SubText>
                  </S.BoardGridWrap>

                  {selectedCell ? (
                    <S.CellPanel>
                      <S.CellPanelTitle>칸 설정</S.CellPanelTitle>

                      <S.Field>
                        <S.Label>
                          칸 제목 <span className="required">*</span>
                        </S.Label>
                        <S.TextInput
                          placeholder="칸 제목을 입력하세요"
                          value={selectedCell.name}
                          disabled={disabled}
                          onChange={(e) =>
                            updateCell(selectedCell.key, {
                              name: e.target.value,
                            })
                          }
                        />
                      </S.Field>

                      <S.Field>
                        <S.Label>진행 방식</S.Label>
                        <S.RadioGroup>
                          <S.RadioOption>
                            <input
                              type="radio"
                              checked={
                                selectedCell.type === MissionBingoColumnType.ONE_OFF
                              }
                              disabled={disabled}
                              onChange={() =>
                                updateCell(selectedCell.key, {
                                  type: MissionBingoColumnType.ONE_OFF,
                                })
                              }
                            />
                            완료형
                          </S.RadioOption>
                          <S.RadioOption>
                            <input
                              type="radio"
                              checked={
                                selectedCell.type ===
                                MissionBingoColumnType.CUMULATIVE
                              }
                              disabled={disabled}
                              onChange={() =>
                                updateCell(selectedCell.key, {
                                  type: MissionBingoColumnType.CUMULATIVE,
                                })
                              }
                            />
                            누적형
                          </S.RadioOption>
                        </S.RadioGroup>
                        <S.SubText>직원이 완료 처리합니다.</S.SubText>
                      </S.Field>

                      {selectedCell.type === MissionBingoColumnType.CUMULATIVE && (
                        <S.Field>
                          <S.Label>
                            목표 횟수 <span className="required">*</span>
                          </S.Label>
                          <S.TextInput
                            type="number"
                            min={1}
                            value={selectedCell.count}
                            disabled={disabled}
                            onChange={(e) =>
                              updateCell(selectedCell.key, {
                                count: e.target.value,
                              })
                            }
                          />
                        </S.Field>
                      )}
                    </S.CellPanel>
                  ) : (
                    <S.EmptyCellPanel>
                      칸을 선택하면 설정할 수 있습니다.
                    </S.EmptyCellPanel>
                  )}
                </S.BoardLayout>
              </S.Section>

              <S.Section>
                <S.StageHeader>
                  <S.SectionTitle>보상 단계 설정</S.SectionTitle>
                  <S.AddStageButton
                    type="button"
                    disabled={disabled}
                    onClick={addStage}
                  >
                    + 단계 추가
                  </S.AddStageButton>
                </S.StageHeader>

                <S.StageList>
                  {form.stages.map((stage, stageIndex) => {
                    const hasPointReward = stage.rewards.some(
                      (reward) =>
                        reward.type === MissionBingoRowRewardDetailType.POINT,
                    );

                    return (
                      <S.StageCard key={stage.key}>
                        <S.StageCardHeader $divided>
                          <div className="left">
                            <S.StageBadge>단계 {stageIndex + 1}</S.StageBadge>
                            <S.Label>달성 줄 수</S.Label>
                            <S.TextInput
                              type="number"
                              min={1}
                              max={maxLineCount}
                              value={stage.row}
                              disabled={disabled}
                              style={{ width: 80 }}
                              onChange={(e) =>
                                updateStage(stage.key, { row: e.target.value })
                              }
                            />
                            <S.SubText>최대 {maxLineCount}줄</S.SubText>
                          </div>
                          <div className="actions">
                            <S.SmallButton
                              type="button"
                              className="primary"
                              disabled={disabled}
                              onClick={() => addStageReward(stage.key)}
                            >
                              + 보상 추가
                            </S.SmallButton>
                            {form.stages.length > 1 && (
                              <S.SmallButton
                                type="button"
                                className="danger"
                                disabled={disabled}
                                onClick={() => removeStage(stage.key)}
                              >
                                단계 삭제
                              </S.SmallButton>
                            )}
                          </div>
                        </S.StageCardHeader>

                        {stage.rewards.map((reward, rewardIndex) => (
                          <S.RewardCard key={reward.key}>
                            <S.StageCardHeader>
                              <div className="left">
                                <S.Label>보상 {rewardIndex + 1}</S.Label>
                              </div>
                              {stage.rewards.length > 1 && (
                                <S.SmallButton
                                  type="button"
                                  className="danger"
                                  disabled={disabled}
                                  onClick={() =>
                                    removeStageReward(stage.key, reward.key)
                                  }
                                >
                                  삭제
                                </S.SmallButton>
                              )}
                            </S.StageCardHeader>

                            <S.RadioGroup>
                              <S.RadioOption>
                                <input
                                  type="radio"
                                  checked={
                                    reward.type ===
                                    MissionBingoRowRewardDetailType.POINT
                                  }
                                  disabled={
                                    disabled ||
                                    (hasPointReward &&
                                      reward.type !==
                                        MissionBingoRowRewardDetailType.POINT)
                                  }
                                  onChange={() =>
                                    updateStageReward(stage.key, reward.key, {
                                      type: MissionBingoRowRewardDetailType.POINT,
                                      count: reward.count ?? 1,
                                      name: undefined,
                                    })
                                  }
                                />
                                매장이용권
                              </S.RadioOption>
                              <S.RadioOption>
                                <input
                                  type="radio"
                                  checked={
                                    reward.type ===
                                    MissionBingoRowRewardDetailType.OTHER
                                  }
                                  disabled={disabled}
                                  onChange={() =>
                                    updateStageReward(stage.key, reward.key, {
                                      type: MissionBingoRowRewardDetailType.OTHER,
                                      name: reward.name ?? "",
                                      count: undefined,
                                    })
                                  }
                                />
                                기타 보상
                              </S.RadioOption>
                            </S.RadioGroup>

                            {reward.type ===
                            MissionBingoRowRewardDetailType.POINT ? (
                              <S.Field>
                                <S.Label>지급 수량</S.Label>
                                <S.TextInput
                                  type="number"
                                  min={1}
                                  value={reward.count ?? ""}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    updateStageReward(stage.key, reward.key, {
                                      count: Number(e.target.value),
                                    })
                                  }
                                />
                                <S.SubText>
                                  유저 앱에서 &apos;보상받기&apos;를 누르면 자동
                                  지급됩니다.
                                </S.SubText>
                              </S.Field>
                            ) : (
                              <S.Field>
                                <S.Label>보상명</S.Label>
                                <S.TextInput
                                  placeholder="보상명을 입력하세요"
                                  value={reward.name ?? ""}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    updateStageReward(stage.key, reward.key, {
                                      name: e.target.value,
                                    })
                                  }
                                />
                              </S.Field>
                            )}
                          </S.RewardCard>
                        ))}
                      </S.StageCard>
                    );
                  })}
                </S.StageList>
              </S.Section>
            </S.Body>

            <S.Footer>
              <S.CancelButton type="button" onClick={onClose}>
                취소
              </S.CancelButton>
              <S.SubmitButton
                type="button"
                disabled={disabled || submitting}
                onClick={submit}
              >
                {isEdit ? "수정" : "등록"}
              </S.SubmitButton>
            </S.Footer>
          </>
        )}
      </S.Popup>

      {isResetConfirmOpen && (
        <ProgressResetConfirmPopup
          title="진행 초기화"
          description="모든 유저의 빙고 진행 상황이 초기화되며, 지급된 보상도 취소·회수될 수 있습니다."
          confirmPhrase={BINGO_RESET_PHRASE}
          submitting={resetting}
          onClose={() => {
            if (!resetting) setIsResetConfirmOpen(false);
          }}
          onConfirm={handleResetConfirm}
        />
      )}
    </Overlay>
  );
};

export default BingoFormPopup;
